/**
 * StorageManager — single page for everything storage-related.
 *
 * Three sections, top to bottom:
 *   1. Usage gauge — used / cap, with a coloured bar + "X% used" + a
 *      breakdown of where the cap comes from (plan + each active add-on).
 *   2. Storage by match — table sorted by total bytes desc. Each row has
 *      "Purge videos" (drops video clips, keeps stats) and a link to the
 *      match scorecard. The biggest 5 matches usually account for >80% of
 *      storage, so this is the highest-value page in the whole flow.
 *   3. Add or manage storage — buyable tier grid + list of active add-ons
 *      with cancel buttons.
 *
 * Built specifically to give a customer a clear, fair choice between
 * "buy more storage" and "delete old footage I don't need" when they hit
 * the cap. Without the breakdown table, every customer just buys the add-on
 * by default — better revenue short term but resentful customers when they
 * realise they could have kept the stats and freed 80% of the space.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  HardDrive, Trash2, Film, Plus, X, AlertTriangle,
  ChevronLeft, Loader2, CheckCircle2,
} from "lucide-react";
import {
  getWalletStorage, getStorageTiers, getStorageAddons,
  cancelStorageAddon, purgeMatchVideos,
} from "../../api/wallet.api";
import { checkoutSubscription } from "../../api/payments.api";

const fmtBytes = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export default function StorageManager() {
  const [overview, setOverview] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(""); // "purchase:STORAGE_5GB" or "purge:<match_id>" or "cancel:<sub_id>"
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [ov, tr] = await Promise.all([getWalletStorage(), getStorageTiers()]);
      setOverview(ov.data);
      setTiers(tr.data?.tiers || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load storage info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const pctUsed = useMemo(() => {
    if (!overview || !overview.cap_bytes) return 0;
    return Math.min(100, (overview.used_bytes / overview.cap_bytes) * 100);
  }, [overview]);

  const barColour = pctUsed >= 95 ? "bg-red-500"
    : pctUsed >= 80 ? "bg-amber-500"
    : "bg-emerald-500";

  const handlePurchase = async (tier) => {
    setBusy(`purchase:${tier.plan_code}`);
    setError("");
    try {
      // Storage add-ons are recurring INR subscriptions — same Cashfree
      // Subscriptions flow as the main plan. Redirect to the hosted
      // mandate page; webhook flips the addon to ACTIVE on first debit.
      const res = await checkoutSubscription(tier.plan_code, window.location.href);
      const { authorisation_url } = res.data;
      if (!authorisation_url) {
        setError(
          "Cashfree didn't return an authorisation URL. " +
          "The storage plan may be missing a cashfree_plan_id."
        );
        return;
      }
      window.location.assign(authorisation_url);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Purchase failed");
    } finally {
      setBusy("");
    }
  };

  const handleCancel = async (sub) => {
    if (!confirm(
      `Cancel your +${sub.gb} GB add-on (₹${sub.price_inr}/mo)?\n\n` +
      `Your data is preserved. You won't be billed next cycle. ` +
      `If you stay over your included cap after the cycle ends, old ` +
      `recordings may be auto-deleted starting from the largest match ` +
      `after 90 days.`
    )) return;
    setBusy(`cancel:${sub.subscription_id}`);
    setError("");
    try {
      await cancelStorageAddon(sub.subscription_id);
      setToast(`Cancelled. +${sub.gb} GB removed from your cap.`);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusy("");
    }
  };

  const handlePurge = async (match) => {
    if (!confirm(
      `Delete every video clip and the master recording for "${match.name}"?\n\n` +
      `The scorecard, ball-by-ball data, and all stats will be KEPT — ` +
      `only the heavy video assets get removed.\n\n` +
      `This will free ${fmtBytes(match.total_bytes)}. This cannot be undone.`
    )) return;
    setBusy(`purge:${match.match_id}`);
    setError("");
    try {
      const res = await purgeMatchVideos(match.match_id);
      setToast(
        `Freed ${fmtBytes(res.data.bytes_freed)} ` +
        `(${res.data.videos_deleted} clips, ${res.data.recordings_deleted} master).`
      );
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Purge failed");
    } finally {
      setBusy("");
    }
  };

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-text-secondary animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      {/* Back button */}
      <Link
        to="/account/wallet"
        className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-4"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to wallet
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary">Storage</h1>
          <p className="text-sm text-text-secondary mt-1">
            Manage your match videos and storage add-ons.
          </p>
        </div>
      </div>

      {/* Error + toast */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2 text-sm text-red-300">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
          <button className="ml-auto" onClick={() => setError("")}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {toast && (
        <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-start gap-2 text-sm text-emerald-300">
          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{toast}</span>
          <button className="ml-auto" onClick={() => setToast("")}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* SECTION 1: Usage gauge */}
      <section className="bg-bg-primary border border-border-default rounded-xl p-4 sm:p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <HardDrive className="w-5 h-5 text-text-secondary" />
          <h2 className="text-lg font-semibold text-text-primary">Usage</h2>
        </div>
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-3xl font-bold text-text-primary tabular-nums">
            {fmtBytes(overview?.used_bytes ?? 0)}
          </span>
          <span className="text-sm text-text-secondary tabular-nums">
            of {fmtBytes(overview?.cap_bytes ?? 0)} ({pctUsed.toFixed(1)}%)
          </span>
        </div>
        <div className="w-full bg-bg-secondary rounded-full h-3 overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${barColour}`}
            style={{ width: `${pctUsed}%` }}
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs text-text-secondary">
          <span className="px-2 py-1 bg-bg-secondary rounded">
            Plan included: <span className="text-text-primary font-medium tabular-nums">{overview?.main_plan_gb ?? 0} GB</span>
          </span>
          {(overview?.addons || []).map((a) => (
            <span key={a.subscription_id} className="px-2 py-1 bg-bg-secondary rounded">
              Add-on: <span className="text-text-primary font-medium tabular-nums">+{a.gb} GB</span>
              {a.status !== "ACTIVE" && (
                <span className="ml-1 text-amber-400">({a.status})</span>
              )}
            </span>
          ))}
        </div>
        {pctUsed >= 80 && pctUsed < 95 && (
          <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-300">
            You're at {pctUsed.toFixed(0)}% capacity. Either delete old match
            videos below, or add more storage at the bottom of this page.
          </div>
        )}
        {pctUsed >= 95 && (
          <div className="mt-3 p-3 bg-rose-500/15 border-2 border-rose-500/50 rounded text-sm text-rose-200 animate-pulse">
            <div className="font-semibold mb-1">⚠ Storage critical — {pctUsed.toFixed(0)}% used.</div>
            <p className="text-xs leading-snug">
              New ball clips will start failing once you hit 100%. Free up
              space now using the table below, or buy a storage add-on at
              the bottom of this page.
            </p>
          </div>
        )}
      </section>

      {/* SECTION 2: Storage by match */}
      <section className="bg-bg-primary border border-border-default rounded-xl p-4 sm:p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Film className="w-5 h-5 text-text-secondary" />
          <h2 className="text-lg font-semibold text-text-primary">Storage by match</h2>
          <span className="text-xs text-text-secondary ml-auto">
            Largest first. Purging keeps stats, only deletes videos.
          </span>
        </div>
        {(overview?.breakdown || []).length === 0 ? (
          <p className="text-sm text-text-secondary text-center py-8">
            No matches with video storage yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-text-secondary border-b border-bg-secondary">
                  <th className="py-2 pr-4 font-medium">Match</th>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium tabular-nums text-right">Ball clips</th>
                  <th className="py-2 pr-4 font-medium tabular-nums text-right">Master</th>
                  <th className="py-2 pr-4 font-medium tabular-nums text-right">Total</th>
                  <th className="py-2 pl-4 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {overview.breakdown.map((m) => (
                  <tr key={m.match_id} className="border-b border-bg-secondary/40 hover:bg-bg-secondary/20">
                    <td className="py-3 pr-4 text-text-primary">
                      <Link
                        to={`/cricket/matches/${m.match_id}/scorecard`}
                        className="hover:underline"
                      >
                        {m.name}
                      </Link>
                    </td>
                    <td className="py-3 pr-4 text-text-secondary">
                      {m.match_date || "—"}
                    </td>
                    <td className="py-3 pr-4 text-text-secondary tabular-nums text-right">
                      {fmtBytes(m.videos_bytes)}
                    </td>
                    <td className="py-3 pr-4 text-text-secondary tabular-nums text-right">
                      {fmtBytes(m.recording_bytes)}
                    </td>
                    <td className="py-3 pr-4 text-text-primary tabular-nums text-right font-medium">
                      {fmtBytes(m.total_bytes)}
                    </td>
                    <td className="py-3 pl-4 text-right">
                      <button
                        onClick={() => handlePurge(m)}
                        disabled={busy === `purge:${m.match_id}`}
                        className="inline-flex items-center gap-1.5 text-xs text-red-300 hover:text-red-200 hover:bg-red-500/10 px-2 py-1 rounded disabled:opacity-50"
                      >
                        {busy === `purge:${m.match_id}`
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Trash2 className="w-3 h-3" />}
                        Purge videos
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* SECTION 3: Active add-ons (only if any) */}
      {(overview?.addons || []).length > 0 && (
        <section className="bg-bg-primary border border-border-default rounded-xl p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Active add-ons</h2>
          <div className="space-y-2">
            {overview.addons.map((a) => (
              <div
                key={a.subscription_id}
                className="flex items-center justify-between p-3 bg-bg-secondary/40 rounded-lg"
              >
                <div>
                  <span className="text-text-primary font-medium tabular-nums">+{a.gb} GB</span>
                  <span className="text-text-secondary text-sm ml-2">
                    ₹{a.price_inr}/month
                  </span>
                  {a.status !== "ACTIVE" && (
                    <span className="ml-2 text-xs text-amber-400">({a.status})</span>
                  )}
                  {a.current_period_end && (
                    <span className="ml-2 text-xs text-text-secondary">
                      next renewal: {a.current_period_end.slice(0, 10)}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleCancel(a)}
                  disabled={busy === `cancel:${a.subscription_id}`}
                  className="text-xs text-red-300 hover:text-red-200 hover:bg-red-500/10 px-2 py-1 rounded disabled:opacity-50"
                >
                  {busy === `cancel:${a.subscription_id}`
                    ? "Cancelling…" : "Cancel"}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SECTION 4: Buy more storage */}
      <section className="bg-bg-primary border border-border-default rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-2">
          <Plus className="w-5 h-5 text-text-secondary" />
          <h2 className="text-lg font-semibold text-text-primary">Add more storage</h2>
        </div>
        <p className="text-xs text-text-secondary mb-4">
          Recurring monthly. Cancel anytime — your data is kept for 90 days
          after cancellation. Stacks with your plan and any other add-on.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {tiers.map((t) => (
            <button
              key={t.plan_code}
              onClick={() => handlePurchase(t)}
              disabled={busy === `purchase:${t.plan_code}`}
              className="text-left p-4 bg-bg-secondary/40 hover:bg-bg-secondary border border-border-default hover:border-button-primary rounded-lg disabled:opacity-50 transition-all"
            >
              <div className="text-xl font-bold text-text-primary tabular-nums">
                +{t.gb} GB
              </div>
              <div className="text-sm text-text-secondary mt-1">
                ₹{t.price_inr}<span className="text-xs">/mo</span>
              </div>
              <div className="text-xs text-text-secondary mt-2 tabular-nums">
                ₹{t.price_per_gb_inr}/GB
              </div>
              {busy === `purchase:${t.plan_code}` && (
                <Loader2 className="w-4 h-4 mt-2 animate-spin text-text-secondary" />
              )}
            </button>
          ))}
        </div>
        <p className="text-xs text-text-secondary mt-4">
          Need more than 100 GB? <a href="mailto:a.manivannan@sportingzenminds.com?subject=ZenCricketData storage enquiry" className="text-purple-400 hover:underline">Contact sales</a> for custom plans.
        </p>
      </section>
    </div>
  );
}
